import type { Address } from '../../../api/types';
import { countryName } from '../format';
import { COUNTRY_CODES, formatOf, type AddressFormat } from './formats';

// Structured address <-> the way its country writes it (TF-23): the form's rows, the lines on the
// page and in "Copy address", and the postal code check. Pure functions, no React, so contact
// addresses (TF-37) and document generation reuse them.

export type AddressPart = 'line1' | 'line2' | 'postalCode' | 'city' | 'region';
type Token = { part: AddressPart } | { text: string };

const CODES: Record<string, AddressPart> = { '%C': 'city', '%S': 'region', '%Z': 'postalCode' };
const parsed = new Map<string, Token[][]>();

/** A format's lines as parts and fixed text; %A stands alone on its line and is two lines, line 1 and line 2. */
function tokens(format: AddressFormat): Token[][] {
  let lines = parsed.get(format.lines);
  if (!lines) {
    lines = format.lines.split('%n').flatMap((line): Token[][] =>
      line === '%A'
        ? [[{ part: 'line1' }], [{ part: 'line2' }]]
        : [
            line
              .split(/(%[CSZ])/)
              .filter(Boolean)
              .map((piece) => (CODES[piece] ? { part: CODES[piece] } : { text: piece })),
          ],
    );
    parsed.set(format.lines, lines);
  }
  return lines;
}

const isPart = (token: Token): token is { part: AddressPart } => 'part' in token;

/** The form's rows for a country: its parts in the order it writes them, the parts of one line side by side. */
export function formRows(country: string | null | undefined): AddressPart[][] {
  return tokens(formatOf(country))
    .map((line) => line.filter(isPart).map((token) => token.part))
    .filter((row) => row.length > 0);
}

/** The address as its country writes it, a line each, with the country's name last. */
export function addressLines(address: Address | null | undefined, locale: string): string[] {
  if (!address) return [];
  const lines = tokens(formatOf(address.country))
    .filter((line) => line.some((token) => isPart(token) && address[token.part]))
    .map((line) =>
      line
        .map((token) => (isPart(token) ? (address[token.part] ?? '') : token.text))
        .join('')
        // A part left empty leaves its separators behind: "Springfield,  62701", ", IL 62701".
        .replace(/\s+/g, ' ')
        .replace(/\s+,/g, ',')
        .replace(/,(\s*,)+/g, ',')
        .replace(/^[\s,]+|[\s,]+$/g, ''),
    )
    .filter(Boolean);
  const country = countryName(address.country ?? null, locale);
  return country ? [...lines, country] : lines;
}

/** On one line, as on the page: "Brīvības iela 1, Rīga, LV-1010, Latvia". */
export const addressOneLine = (address: Address | null | undefined, locale: string) => addressLines(address, locale).join(', ');

/**
 * A typed postal code tidied (capitals, the country's prefix added: 1010 -> LV-1010) and checked
 * against the country's pattern, where one is known.
 */
export function checkPostalCode(country: string | null | undefined, typed: string): { value: string; ok: boolean } {
  const format = formatOf(country);
  let value = typed.trim().replace(/\s+/g, ' ').toUpperCase();
  if (!value) return { value, ok: true };
  if (format.zipPrefix && /^\d+$/.test(value)) value = format.zipPrefix + value;
  return { value, ok: !format.zip || new RegExp(`^(?:${format.zip})$`, 'i').test(value) };
}

const countryLists = new Map<string, { code: string; name: string }[]>();

/** Every country, named in the app language, in its alphabetical order. */
export function countryOptions(locale: string): { code: string; name: string }[] {
  let list = countryLists.get(locale);
  if (!list) {
    const collator = new Intl.Collator(locale);
    list = COUNTRY_CODES.map((code) => ({ code, name: countryName(code, locale) })).sort((a, b) => collator.compare(a.name, b.name));
    countryLists.set(locale, list);
  }
  return list;
}
